import type { Voices } from './instruments.js';
import { INSTRUMENTS, makeNoise, makeRoom, SENDS } from './instruments.js';
import type { InstrumentName, NoteEvent, Track } from './score.js';
import { hz, swung } from './score.js';
import type { TrackId } from './tracks.js';
import { track } from './tracks.js';

/**
 * The music (ADR-0056): a loop for the menu and one for each building, played
 * on the context the game's sounds already use, a few notes ahead of time.
 *
 * Under the sounds on purpose: the tracks were measured at one loudness on a
 * phone, played about 15 dB under the door chime, and everything the music does follows the screen —
 * quieter while paused, a little quicker in a shift's last ten seconds,
 * silent while the page is out of sight. It decides nothing about the game.
 */

/**
 * The music's level, before each track's trim. The mockups played it at 0.5,
 * about 9 dB under the door chime; the owner heard it in play as far too loud
 * and asked for 60–70% of it (PLAYTEST_LOG 2026-10-29). Half the gain is 6 dB
 * less, about two-thirds as loud to the ear, about 15 dB under the chime.
 */
const LEVEL = 0.25;
/** Paused, the music steps back to this share of its level. */
const DUCKED = 0.4;
/** The last ten seconds of a clock: this much quicker. */
const HURRY = 1.1;
/** How far ahead notes are handed to the audio clock, and how often. */
const LOOKAHEAD_S = 0.2;
const TICK_MS = 25;
/** One track giving way to the next. */
const FADE_S = 0.6;

interface Master {
  readonly input: GainNode;
  readonly noise: AudioBuffer;
  readonly room: AudioBuffer;
}

function makeMaster(c: AudioContext): Master {
  const input = c.createGain();
  input.gain.value = LEVEL;
  const glue = c.createDynamicsCompressor();
  glue.threshold.value = -20;
  glue.knee.value = 10;
  glue.ratio.value = 2.5;
  glue.attack.value = 0.01;
  glue.release.value = 0.25;
  input.connect(glue);
  glue.connect(c.destination);
  return { input, noise: makeNoise(c), room: makeRoom(c, 2.2) };
}

/** One track playing: its own buses and room, faded in, and out when it is done. */
class Player {
  private readonly out: GainNode;
  private readonly voices: Voices;
  private readonly length: number;
  private readonly timer: ReturnType<typeof setInterval>;
  private index = 0;
  private loop = 0;
  private anchorTime: number;
  private anchorBeat = 0;
  private secondsPerBeat: number;

  constructor(
    private readonly c: AudioContext,
    master: Master,
    readonly track: Track,
    hurry: boolean,
  ) {
    const at = c.currentTime + 0.08;
    this.out = c.createGain();
    this.out.gain.setValueAtTime(0, at);
    this.out.gain.linearRampToValueAtTime(track.trim, at + 0.4);
    this.out.connect(master.input);
    const room = c.createConvolver();
    room.buffer = master.room;
    const wet = c.createGain();
    wet.gain.value = 0.6;
    room.connect(wet);
    wet.connect(this.out);
    const buses = new Map<InstrumentName, GainNode>();
    this.voices = {
      c,
      noise: master.noise,
      bus: (name) => {
        const known = buses.get(name);
        if (known) return known;
        const bus = c.createGain();
        bus.connect(this.out);
        const send = c.createGain();
        send.gain.value = SENDS[name];
        bus.connect(send);
        send.connect(room);
        if (name === 'vibes') this.tremolo(bus, at);
        buses.set(name, bus);
        return bus;
      },
    };
    this.length = track.bars * track.beatsPerBar;
    this.anchorTime = at;
    this.secondsPerBeat = 60 / (track.bpm * (hurry ? HURRY : 1));
    this.timer = setInterval(() => this.pump(), TICK_MS);
    this.pump();
  }

  /** The vibraphone's tremolo, on the whole instrument. */
  private tremolo(bus: GainNode, at: number): void {
    bus.gain.value = 0.85;
    const lfo = this.c.createOscillator();
    lfo.frequency.value = 5.2;
    const depth = this.c.createGain();
    depth.gain.value = 0.15;
    lfo.connect(depth);
    depth.connect(bus.gain);
    lfo.start(at);
  }

  /** Hand the audio clock every note due before the horizon. */
  private pump(): void {
    const horizon = this.c.currentTime + LOOKAHEAD_S;
    const events = this.track.events;
    for (;;) {
      const e = events[this.index];
      if (!e) return;
      const beat = this.loop * this.length + swung(e.beat, this.track.swing);
      const t = this.anchorTime + (beat - this.anchorBeat) * this.secondsPerBeat;
      if (t > horizon) return;
      // A note already past (a tab coming back, a slow frame) is skipped, not crammed in.
      if (t >= this.c.currentTime - 0.03) this.sound(e, t);
      this.index += 1;
      if (this.index >= events.length) {
        this.index = 0;
        this.loop += 1;
      }
    }
  }

  private sound(e: NoteEvent, t: number): void {
    try {
      const seconds = Math.max(0.05, e.beats * this.secondsPerBeat);
      INSTRUMENTS[e.instrument](
        this.voices,
        t + e.offset,
        e.midi === null ? 0 : hz(e.midi),
        seconds,
        e.velocity,
      );
    } catch {
      // A note that fails is a missing note, never a broken game.
    }
  }

  /** Quicker or back to time, from now: the notes already handed over keep theirs. */
  hurry(on: boolean): void {
    const now = this.c.currentTime;
    this.anchorBeat += (now - this.anchorTime) / this.secondsPerBeat;
    this.anchorTime = now;
    this.secondsPerBeat = 60 / (this.track.bpm * (on ? HURRY : 1));
  }

  stop(): void {
    clearInterval(this.timer);
    const now = this.c.currentTime;
    this.out.gain.cancelScheduledValues(now);
    this.out.gain.setValueAtTime(this.out.gain.value, now);
    this.out.gain.linearRampToValueAtTime(0, now + FADE_S);
    setTimeout(() => this.out.disconnect(), (FADE_S + 0.3) * 1000);
  }
}

export class Music {
  private context: AudioContext | null = null;
  private master: Master | null = null;
  private player: Player | null = null;
  private wanted: TrackId | null = null;
  private enabled = true;
  private hidden = false;
  private hurried = false;
  private ducked = false;

  /** The game's audio context, once a tap has made one. Safe to call every time. */
  attach(context: AudioContext | null): void {
    if (!context || context === this.context) return;
    this.context = context;
    try {
      this.master = makeMaster(context);
    } catch {
      this.master = null;
    }
    this.sync();
  }

  /** What should play now; null for nothing. The same track keeps playing. */
  play(id: TrackId | null): void {
    this.wanted = id;
    this.sync();
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    this.sync();
  }

  /** The page out of sight: stop, and start the track again on the way back. */
  setHidden(hidden: boolean): void {
    this.hidden = hidden;
    this.sync();
  }

  /** A shift's last ten seconds. */
  setHurry(on: boolean): void {
    if (on === this.hurried) return;
    this.hurried = on;
    this.player?.hurry(on);
  }

  /** Paused: the music steps back, and comes forward again on the way out. */
  setDucked(on: boolean): void {
    if (on === this.ducked) return;
    this.ducked = on;
    const master = this.master;
    if (!master || !this.context) return;
    master.input.gain.setTargetAtTime(LEVEL * (on ? DUCKED : 1), this.context.currentTime, 0.15);
  }

  private sync(): void {
    const context = this.context;
    const master = this.master;
    if (!context || !master) return;
    const target = this.enabled && !this.hidden ? this.wanted : null;
    if (this.player?.track.id === target) return;
    this.player?.stop();
    this.player = null;
    if (target === null) return;
    try {
      this.player = new Player(context, master, track(target), this.hurried);
    } catch {
      // A track that cannot be written or played leaves the game silent, never broken.
      this.player = null;
    }
  }
}
